import { When } from 'react-if';
import { Tooltip } from '@mui/material';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { displayPhoneNumber } from 'shared';

import { PillBadge } from '../../../../common/Atoms/PillBadge';
import { Strap } from '../../../../common/Atoms/Strap';
import { ChildFields } from '../../../../common/Components/Invoice/ChildFields';
import { InvoicePaper } from '../../../../common/Components/Invoice/InvoicePaper';
import { useInvoiceDetails } from '../../../../common/hooks/useInvoiceDetails';
import { useGetSelectedOrganisation } from '../../../hooks/useGetSelectedOrganisation';
import { useInvoice } from '../../../hooks/useInvoice';
import { useSettings } from '../../../hooks/useSettings';
import { WrappedLink } from '../../WrappedLink';
import { InvoiceAttachments } from '../InvoiceDocument/InvoiceAttachments';

import { InvoicePaymentPlanStrip } from './InvoicePaymentPlanStrip';

/** The invoice laid out as a paper document */
export function InvoicePaperV2() {
  const { invoice } = useInvoice();
  const organisation = useGetSelectedOrganisation();
  const { data: settings } = useSettings();
  const details = useInvoiceDetails(invoice);

  if (!details) return null;

  const person = details.contact.primaryContactPerson;
  const email = details.invoiceEmail || person?.email;
  const contactLine = [
    email,
    displayPhoneNumber(person?.mobileCountryCode, person?.mobileNumber),
    displayPhoneNumber(person?.defaultCountryCode, person?.defaultNumber),
  ].filter(Boolean).join(` · `);

  return (
    <InvoicePaper
      mode={ `app` }
      invoice={ invoice }
      details={ details }
      sender={ {
        customerFacingCompanyName: organisation.legalName,
        companyNumberType: settings?.companyNumberType,
        companyNumber: settings?.companyNumber,
        taxNumber: settings?.taxNumber,
      } }
      top={ (
        <>
          <When condition={ details.createdByUs }>
            <Strap type={ `warning` }>
              { `This invoice was created by Paidnice. No invoice based late fee or discount policies will apply to it` }
            </Strap>
          </When>
          <When condition={ details.frozen }>
            <Strap type={ `frozen` }>
              { `This invoice is frozen. No policies will apply to it` }
            </Strap>
          </When>
          <When condition={ details.deleted }>
            <Strap type={ `error` }>
              { `This invoice is deleted.` }
            </Strap>
          </When>
          <When condition={ details.processingStripePayments?.length > 0 }>
            <Strap type={ `caution` }>
              { `This invoice has ${details.processingStripePayments.length} payment(s) in progress through Stripe.` }
            </Strap>
          </When>

          <InvoicePaymentPlanStrip />
        </>
      ) }
      billTo={ (
        <>
          <span className={ `flex flex-wrap items-center gap-2` }>
            <WrappedLink
              to={ `/contacts/${details.contact.id}` }
              className={ `text-[0.9375rem] font-semibold text-paidniceGreen-600 hover:text-paidniceGreen-700 hover:underline` }
            >
              { details.contact.name }
            </WrappedLink>
            <When condition={ !!details.groupTitle }>
              <Tooltip title={ `The Paidnice group that this contact belongs to` }>
                <PillBadge
                  shape={ `tag` }
                  className={ `text-gray-600` }
                >
                  { details.groupTitle }
                </PillBadge>
              </Tooltip>
            </When>
            <When condition={ !!details.sent_to_customer }>
              <Tooltip title={ `This invoice has been sent` }>
                <PillBadge
                  shape={ `tag` }
                  className={ `text-gray-600` }
                >
                  { `Sent` }
                </PillBadge>
              </Tooltip>
            </When>
            <When condition={ !!invoice.contact?.frozen }>
              <Tooltip title={ `Contact has been frozen` }>
                <span>
                  <PillBadge
                    shape={ `tag` }
                    color={ `blue` }
                  >
                    { `Contact Frozen` }
                  </PillBadge>
                </span>
              </Tooltip>
            </When>
          </span>
          <When condition={ !!details.contact.externalAccountNumber && details.contact.externalAccountNumber !== details.contact.name }>
            <span className={ `text-sm text-gray-500` }>{ details.contact.externalAccountNumber }</span>
          </When>
          <span className={ `flex items-center gap-1 text-sm text-gray-600` }>
            { contactLine }
            <When condition={ !!details.invoiceEmail && details.invoiceEmail !== person?.email }>
              <Tooltip title={ `This email is set on the invoice and overrides the contact's default email (${person?.email})` }>
                <ExclamationCircleIcon className={ `h-4 w-4 shrink-0 cursor-help text-amber-500` } />
              </Tooltip>
            </When>
          </span>
          <When condition={ !!details.invoiceEmailCc }>
            <span className={ `text-sm text-gray-500` }>{ `CC: ${details.invoiceEmailCc}` }</span>
          </When>
          <When condition={ !!details.invoiceEmailBcc }>
            <span className={ `text-sm text-gray-500` }>{ `BCC: ${details.invoiceEmailBcc}` }</span>
          </When>
          <When condition={ !!details.contact.tax_number }>
            <span className={ `text-sm text-gray-500` }>{ `Tax number: ${details.contact.tax_number}` }</span>
          </When>
          <ChildFields childFields={ details.childFields } />
        </>
      ) }
      bottom={ <InvoiceAttachments /> }
    />
  );
}
